import { useState, useEffect, useRef, useCallback } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useQuery, QueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Crown, X, Loader2, Plus, Store, ImagePlus, GripVertical } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { ErrorBannerWithId } from "@/components/error-id-chip";
import { ToastFn, VendorProfile, VendorSession } from "./_shared";



// ── Marketplace Tab ───────────────────────────────────────────────────────────

export type MarketplaceSubState = {
  isMarketplaceListed: boolean;
  marketplaceOptedIn?: boolean;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  hasSubscription?: boolean;
  hasLegacySubscription?: boolean;
  isProTier: boolean;
  marketplaceTier: string | null;
  proSubscriptionStatus: string | null;
  proSubscriptionId: string | null;
};


export function SortableWorkPhoto({ url, idx, sortableId, onRemove }: { url: string; idx: number; sortableId: string; onRemove: (idx: number) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`work-photo-${idx}`}>
      <img
        src={url}
        alt={`Work photo ${idx + 1}`}
        className="w-full aspect-square rounded-md object-cover border border-gray-200"
        onError={e => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
      />
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 bg-white/90 hover:bg-white rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        aria-label={`Drag to reorder work photo ${idx + 1}`}
        data-testid={`button-drag-work-photo-${idx}`}
      >
        <GripVertical className="h-3.5 w-3.5 text-gray-500" />
      </button>
      <button
        type="button"
        onClick={() => onRemove(idx)}
        className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={`Remove work photo ${idx + 1}`}
        data-testid={`button-remove-work-photo-${idx}`}
      >
        <X className="h-3.5 w-3.5 text-red-500" />
      </button>
      {idx === 0 && (
        <span className="absolute bottom-1 left-1 bg-brand text-white text-[10px] font-semibold px-1 py-0.5 rounded" data-testid="badge-first-photo">
          1st
        </span>
      )}
    </div>
  );
}


export function MarketplaceTab({ vendor, vendorLoading, vendorId, session, queryClient, toast }: {
  vendor?: VendorProfile;
  vendorLoading: boolean;
  vendorId: number;
  session: VendorSession;
  queryClient: QueryClient;
  toast: ToastFn;
}) {
  const [marketplaceBio, setMarketplaceBio] = useState("");
  const [marketplacePhotoUrl, setMarketplacePhotoUrl] = useState<string>("");
  const [workPhotoUrls, setWorkPhotoUrls] = useState<string[]>([]);
  const [savingMarketplace, setSavingMarketplace] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingWorkPhoto, setUploadingWorkPhoto] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState("");
  const [marketplaceActionPending, setMarketplaceActionPending] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const workPhotoInputRef = useRef<HTMLInputElement>(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleWorkPhotoDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = parseInt((active.id as string).split("::")[1], 10);
    const newIndex = parseInt((over.id as string).split("::")[1], 10);
    if (isNaN(oldIndex) || isNaN(newIndex) || oldIndex === newIndex) return;
    const reordered = arrayMove(workPhotoUrls, oldIndex, newIndex);
    setWorkPhotoUrls(reordered);
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", { workPhotoUrls: reordered });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
    } catch {
      setWorkPhotoUrls(workPhotoUrls);
      toast({ title: "Error", description: "Failed to save photo order.", variant: "destructive" });
    }
  }, [workPhotoUrls, vendorId, queryClient, toast]);

  const downloadFeeReceipt = useCallback(async (fee: { id: number; monthKey: string; chargedCents: number; createdAt: string; taskId: number | null; taskTitle: string | null }) => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryBlue: [number, number, number] = [0, 82, 204];
    const accentOrange: [number, number, number] = [255, 165, 0];

    const [year, month] = fee.monthKey.split("-");
    const monthLabel = new Date(Number(year), Number(month) - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
    const dateLabel = new Date(fee.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const vendorDisplayName = vendor?.businessName || vendor?.name || "Vendor";
    const amount = `$${(fee.chargedCents / 100).toFixed(2)}`;

    doc.setFillColor(...primaryBlue);
    doc.rect(0, 0, pageWidth, 36, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("PadPulse", 14, 18);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Marketplace Fee Receipt", 14, 27);
    doc.text(dateLabel, pageWidth - 14, 18, { align: "right" });

    doc.setFillColor(...accentOrange);
    doc.rect(14, 44, pageWidth - 28, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Receipt Details", 17, 49.5);

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const rows: [string, string][] = [
      ["Vendor", vendorDisplayName],
      ["Billing Period", monthLabel],
      ["Charge Date", dateLabel],
      ["Amount", amount],
    ];
    if (fee.taskId != null) {
      rows.push(["Task", fee.taskTitle ?? `Task #${fee.taskId}`]);
    }

    let y = 62;
    rows.forEach(([label, value], i) => {
      if (i % 2 === 0) {
        doc.setFillColor(245, 247, 250);
        doc.rect(14, y - 4, pageWidth - 28, 9, "F");
      }
      doc.setFont("helvetica", "bold");
      doc.text(label, 17, y + 1.5);
      doc.setFont("helvetica", "normal");
      doc.text(value, pageWidth - 17, y + 1.5, { align: "right" });
      y += 10;
    });

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("PadPulse — padpulse.io", pageWidth / 2, 280, { align: "center" });

    doc.save(`padpulse-marketplace-fee-${fee.monthKey}-receipt.pdf`);
  }, [vendor]);

  const { data: marketplaceSub, refetch: refetchMarketplaceSub } = useQuery<MarketplaceSubState>({
    queryKey: ["/api/vendor-portal/marketplace-subscription"],
    enabled: !!session.isOwner,
  });

  useEffect(() => {
    if (vendor) {
      setMarketplaceBio(vendor.marketplaceBio ?? "");
      setMarketplacePhotoUrl(vendor.marketplacePhotoUrl ?? "");
      const vw = vendor as VendorProfile & { workPhotoUrls?: string[] | null };
      setWorkPhotoUrls(Array.isArray(vw.workPhotoUrls) ? vw.workPhotoUrls : []);
    }
  }, [vendor]);

  const persistMarketplacePhotoUrl = async (newUrl: string | null) => {
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", { marketplacePhotoUrl: newUrl });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      return true;
    } catch (e: unknown) {
      setMarketplaceError(parseErrorMessage(e, "Failed to save marketplace photo"));
      return false;
    }
  };

  const handleUploadMarketplacePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    setMarketplaceError("");
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const data = reader.result as string;
          const r = await fetch("/api/vendor-portal/marketplace-photo", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename: file.name, mimeType: file.type, data }),
          });
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            setMarketplaceError(body?.message || "Photo upload failed");
            return;
          }
          const { url } = await r.json();
          const previousUrl = marketplacePhotoUrl;
          setMarketplacePhotoUrl(url);
          const persisted = await persistMarketplacePhotoUrl(url);
          if (persisted) {
            toast({ title: "Marketplace photo updated" });
          } else {
            setMarketplacePhotoUrl(previousUrl);
          }
        } finally {
          setUploadingPhoto(false);
          if (photoInputRef.current) photoInputRef.current.value = "";
        }
      };
      reader.onerror = () => { setMarketplaceError("Failed to read file"); setUploadingPhoto(false); };
      reader.readAsDataURL(file);
    } catch {
      setMarketplaceError("Photo upload failed");
      setUploadingPhoto(false);
    }
  };

  const handleRemoveMarketplacePhoto = async () => {
    if (uploadingPhoto) return;
    setUploadingPhoto(true);
    setMarketplaceError("");
    const previousUrl = marketplacePhotoUrl;
    try {
      setMarketplacePhotoUrl("");
      const persisted = await persistMarketplacePhotoUrl(null);
      if (persisted) {
        toast({ title: "Marketplace photo removed" });
      } else {
        setMarketplacePhotoUrl(previousUrl);
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleUploadWorkPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    const available = 10 - workPhotoUrls.length;
    if (available <= 0) {
      setMarketplaceError("You can upload a maximum of 10 work photos.");
      if (workPhotoInputRef.current) workPhotoInputRef.current.value = "";
      return;
    }
    const toUpload = files.slice(0, available);
    if (files.length > available) {
      setMarketplaceError(`Only ${available} slot${available === 1 ? "" : "s"} remaining — uploading the first ${available}.`);
    } else {
      setMarketplaceError("");
    }
    setUploadingWorkPhoto(true);

    const uploadFile = (file: File): Promise<string | null> =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const r = await fetch("/api/vendor-portal/marketplace-work-photo", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ filename: file.name, mimeType: file.type, data: reader.result }),
            });
            if (!r.ok) {
              const body = await r.json().catch(() => ({}));
              setMarketplaceError(body?.message || `Failed to upload ${file.name}`);
              resolve(null);
              return;
            }
            const { url } = await r.json();
            resolve(url);
          } catch {
            setMarketplaceError(`Failed to upload ${file.name}`);
            resolve(null);
          }
        };
        reader.onerror = () => { setMarketplaceError("Failed to read file"); resolve(null); };
        reader.readAsDataURL(file);
      });

    try {
      const uploaded: string[] = [];
      for (const file of toUpload) {
        const url = await uploadFile(file);
        if (url) uploaded.push(url);
      }
      if (uploaded.length > 0) {
        const newUrls = [...workPhotoUrls, ...uploaded];
        setWorkPhotoUrls(newUrls);
        try {
          await apiRequest("PATCH", "/api/vendor-portal/profile", { workPhotoUrls: newUrls });
          queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
          queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
          toast({ title: uploaded.length === 1 ? "Work photo added" : `${uploaded.length} work photos added` });
        } catch (e: unknown) {
          setWorkPhotoUrls(workPhotoUrls);
          setMarketplaceError(parseErrorMessage(e, "Failed to save work photos"));
        }
      }
    } finally {
      setUploadingWorkPhoto(false);
      if (workPhotoInputRef.current) workPhotoInputRef.current.value = "";
    }
  };

  const handleRemoveWorkPhoto = async (idx: number) => {
    const newUrls = workPhotoUrls.filter((_, i) => i !== idx);
    setWorkPhotoUrls(newUrls);
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", { workPhotoUrls: newUrls });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      toast({ title: "Work photo removed" });
    } catch (e: unknown) {
      setWorkPhotoUrls(workPhotoUrls);
      setMarketplaceError(parseErrorMessage(e, "Failed to save work photos"));
    }
  };

  const handleSaveMarketplace = async () => {
    setSavingMarketplace(true);
    setMarketplaceError("");
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", {
        marketplaceBio,
        marketplacePhotoUrl: marketplacePhotoUrl || null,
        workPhotoUrls,
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      toast({ title: "Marketplace listing saved" });
    } catch (e: unknown) {
      setMarketplaceError(parseErrorMessage(e, "Failed to update marketplace listing"));
    } finally {
      setSavingMarketplace(false);
    }
  };

  const runMarketplaceAction = async (
    action: "checkout" | "portal" | "pause" | "resume" | "cancel" | "uncancel" | "pro-checkout" | "pro-cancel" | "pro-uncancel" | "pro-downgrade" | "pro-portal",
    successToast?: string,
  ) => {
    setMarketplaceActionPending(action);
    setMarketplaceError("");
    try {
      const r = await apiRequest("POST", `/api/vendor-portal/marketplace-subscription/${action}`, {});
      const data = await r.json();
      if (data?.url) { window.location.href = data.url as string; return; }
      await refetchMarketplaceSub();
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      if (successToast) toast({ title: successToast });
    } catch (e: unknown) {
      setMarketplaceError(parseErrorMessage(e, "Marketplace subscription action failed"));
    } finally {
      setMarketplaceActionPending(null);
    }
  };

  if (vendorLoading) {
    return <p className="text-sm text-gray-400 text-center py-8">Loading...</p>;
  }

  const sub = marketplaceSub;
  const isPro = !!sub?.isProTier;
  const proStatus = sub?.proSubscriptionStatus ?? null;
  // "active_cancel_pending" is written when cancel_at_period_end=true but the
  // subscription is still active — Pro benefits remain until the period ends.
  const proIsCancelingAtEnd = isPro && proStatus === "active_cancel_pending";
  const status = sub?.subscriptionStatus ?? null;
  const periodEnd = sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
  const periodEndLabel = periodEnd
    ? periodEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 pb-1">
        <div className="flex items-center gap-2 p-2 rounded-md bg-brand-accent-muted">
          <Store className="h-4 w-4 text-brand-accent" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Marketplace Listing</h2>
          <p className="text-xs text-gray-500">Manage how you appear to hosts browsing the vendor marketplace.</p>
        </div>
      </div>

      {/* Subscription status card */}
      <Card data-tour="marketplace-subscription">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            Listing Status
            {isPro && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-accent px-2 py-0.5 text-xs font-semibold text-white" data-testid="badge-pro-tier">
                <Crown className="h-3 w-3" />
                Pro
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-gray-500">
            List yourself in the PadPulse Vendor Marketplace so hosts in your area can discover and hire you. <strong>Free to list</strong> — only $19 in months you accept a marketplace job. Or upgrade to Pro for a flat $99/month with no per-job fee.
          </p>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          {(!sub || !sub.hasSubscription) && !isPro && (
            <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3 space-y-2" data-testid="block-marketplace-subscribe">
              <div className="text-sm font-medium text-blue-900 dark:text-blue-200">Get listed — pay only when you work</div>
              <p className="text-xs text-blue-800 dark:text-blue-300">
                <strong>Free to start.</strong> Hosts in your area will see your name, business, services, ratings, bio, and photo.
                You're only charged <strong>$19 in any month you accept a job</strong> from a marketplace host. No jobs that month = $0.
              </p>
              <Button className="w-full" onClick={() => runMarketplaceAction("checkout")} disabled={marketplaceActionPending === "checkout"} data-testid="button-marketplace-subscribe">
                {marketplaceActionPending === "checkout" ? "Opening Stripe..." : "Get listed — $19/month when active"}
              </Button>
            </div>
          )}
          {status === "paused" && (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 space-y-2" data-testid="block-marketplace-paused">
              <div className="text-sm font-medium text-amber-900 dark:text-amber-100">Your listing is paused</div>
              <p className="text-xs text-amber-800 dark:text-amber-200">You're hidden from the marketplace. Resume any time — you're only charged in months you accept a job.</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => runMarketplaceAction("resume", "Listing resumed")} disabled={marketplaceActionPending === "resume"} data-testid="button-marketplace-resume">
                  {marketplaceActionPending === "resume" ? "Resuming..." : "Resume listing"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("portal")} disabled={marketplaceActionPending === "portal"} data-testid="button-marketplace-manage">
                  {marketplaceActionPending === "portal" ? "Opening..." : "Manage billing"}
                </Button>
              </div>
            </div>
          )}
          {(status === "past_due" || status === "unpaid" || status === "incomplete") && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2" data-testid="block-marketplace-past-due">
              <div className="text-sm font-medium text-red-900">Payment failed — listing hidden</div>
              <p className="text-xs text-red-800">A recent $19 charge didn't go through. Update your card to restore your listing.</p>
              <Button size="sm" onClick={() => runMarketplaceAction("portal")} disabled={marketplaceActionPending === "portal"} data-testid="button-marketplace-update-payment">
                {marketplaceActionPending === "portal" ? "Opening..." : "Update payment method"}
              </Button>
            </div>
          )}
          {status === "canceled" && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="block-marketplace-canceled">
              <div className="text-sm font-medium text-gray-900">Subscription canceled</div>
              <p className="text-xs text-gray-600">Re-subscribe at any time to relist yourself. You'll only be charged $19 in months you accept a marketplace job.</p>
              <Button size="sm" onClick={() => runMarketplaceAction("checkout")} disabled={marketplaceActionPending === "checkout"} data-testid="button-marketplace-resubscribe">
                {marketplaceActionPending === "checkout" ? "Opening Stripe..." : "Re-subscribe"}
              </Button>
            </div>
          )}
          {status === "active" && !isPro && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="block-marketplace-active">
              <div className="text-sm font-medium text-emerald-900">Listed in the marketplace</div>
              <p className="text-xs text-emerald-800" data-testid="text-marketplace-renewal">
                You're only charged <strong>$19</strong> in months you accept a job from a marketplace host. No jobs = no charge.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("portal")} disabled={marketplaceActionPending === "portal"} data-testid="button-marketplace-manage">
                  {marketplaceActionPending === "portal" ? "Opening..." : "Manage billing"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("pause", "Listing paused")} disabled={marketplaceActionPending === "pause"} data-testid="button-marketplace-pause">
                  {marketplaceActionPending === "pause" ? "Pausing..." : "Pause listing"}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => runMarketplaceAction("cancel", "Listing will end at period end")} disabled={marketplaceActionPending === "cancel"} data-testid="button-marketplace-cancel">
                  {marketplaceActionPending === "cancel" ? "Cancelling..." : "Cancel"}
                </Button>
              </div>
            </div>
          )}

          {/* ── Pro tier status ── */}
          {isPro && !proIsCancelingAtEnd && (
            <div className="rounded-md border border-brand-accent/30 bg-brand-accent-muted p-3 space-y-2" data-testid="block-marketplace-pro-active">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-brand-accent shrink-0" />
                <span className="text-sm font-semibold text-brand-accent">Pro — active</span>
              </div>
              <p className="text-xs text-gray-700">
                Your listing shows a <strong>Pro badge</strong> visible to hosts. The 3% PadPulse fee and the $19 pay-on-job fee are both waived — you pay a flat $99/month instead.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("pro-portal")} disabled={marketplaceActionPending === "pro-portal"} data-testid="button-pro-manage">
                  {marketplaceActionPending === "pro-portal" ? "Opening..." : "Manage billing"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => runMarketplaceAction("pro-cancel", "Pro will end at period end")}
                  disabled={marketplaceActionPending === "pro-cancel"}
                  data-testid="button-pro-cancel"
                >
                  {marketplaceActionPending === "pro-cancel" ? "Cancelling..." : "Cancel Pro"}
                </Button>
              </div>
            </div>
          )}

          {/* Pro pending cancel — cancel_at_period_end=true; benefits remain active */}
          {proIsCancelingAtEnd && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="block-marketplace-pro-canceling">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-brand-accent shrink-0" />
                <span className="text-sm font-semibold text-amber-900">Pro — cancellation scheduled</span>
              </div>
              <p className="text-xs text-amber-800">Pro will end at the current billing period. Your Pro badge and fee waivers remain active until then.</p>
              <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("pro-uncancel", "Pro cancellation undone")} disabled={marketplaceActionPending === "pro-uncancel"} data-testid="button-pro-uncancel">
                {marketplaceActionPending === "pro-uncancel" ? "Saving..." : "Keep Pro"}
              </Button>
            </div>
          )}

          {/* ── Pro upgrade upsell (shown when listed but not Pro) ── */}
          {!isPro && !proIsCancelingAtEnd && sub?.isMarketplaceListed && (
            <div className="rounded-md border border-brand/20 bg-brand-muted p-3 space-y-2" data-testid="block-marketplace-pro-upsell">
              <div className="flex items-center gap-1.5">
                <Crown className="h-4 w-4 text-brand shrink-0" />
                <span className="text-sm font-semibold text-gray-900">Upgrade to Pro — $99/month</span>
              </div>
              <ul className="text-xs text-gray-700 space-y-1 list-none">
                <li>✓ <strong>Pro badge</strong> on your listing — stand out to hosts</li>
                <li>✓ <strong>No 3% PadPulse fee</strong> deducted from payouts</li>
                <li>✓ <strong>No $19/month</strong> pay-on-job marketplace fee</li>
              </ul>
              <p className="text-xs text-gray-500">Pays for itself after ~$3,300/month in platform jobs. Cancel any time.</p>
              <Button
                size="sm"
                className="bg-brand hover:bg-brand-hover text-white"
                onClick={() => runMarketplaceAction("pro-checkout")}
                disabled={marketplaceActionPending === "pro-checkout"}
                data-testid="button-marketplace-upgrade-pro"
              >
                {marketplaceActionPending === "pro-checkout" ? "Opening Stripe..." : "Upgrade to Pro"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Listing details (bio + main photo) */}
      <Card data-testid="card-marketplace-listing-details">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Listing Details</CardTitle>
          <p className="text-xs text-gray-500">This information appears on your marketplace card. Save when you're done editing.</p>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-4">
          <div>
            <Label htmlFor="textarea-marketplace-bio">Bio</Label>
            <Textarea
              id="textarea-marketplace-bio"
              value={marketplaceBio}
              onChange={e => setMarketplaceBio(e.target.value.slice(0, 600))}
              placeholder="A short pitch hosts will see — what you do, your service area, what makes you stand out."
              rows={4}
              maxLength={600}
              data-testid="textarea-marketplace-bio"
            />
            <p className="text-xs text-gray-400 mt-1 text-right" data-testid="text-marketplace-bio-count">
              {marketplaceBio.length} / 600
            </p>
          </div>

          <div>
            <Label>Profile Photo</Label>
            <div className="flex items-start gap-3 mt-1">
              {marketplacePhotoUrl ? (
                <img
                  src={marketplacePhotoUrl}
                  alt="Marketplace photo"
                  className="w-20 h-20 rounded-md object-cover border border-gray-200"
                  data-testid="img-marketplace-photo"
                  onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div className="w-20 h-20 rounded-md border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-xs text-center px-2" data-testid="placeholder-marketplace-photo">
                  No photo
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleUploadMarketplacePhoto}
                  data-testid="input-marketplace-photo"
                />
                <Button type="button" size="sm" variant="outline" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} data-testid="button-upload-marketplace-photo">
                  {uploadingPhoto ? "Uploading..." : marketplacePhotoUrl ? "Change Photo" : "Upload Photo"}
                </Button>
                {marketplacePhotoUrl && (
                  <Button type="button" size="sm" variant="ghost" onClick={handleRemoveMarketplacePhoto} disabled={uploadingPhoto} data-testid="button-remove-marketplace-photo">
                    Remove
                  </Button>
                )}
                <p className="text-xs text-gray-400">JPG, PNG, WebP, or GIF. Up to 10 MB. Saved automatically on upload.</p>
              </div>
            </div>
          </div>

          {marketplaceError && <ErrorBannerWithId message={marketplaceError} />}
          <Button className="w-full" onClick={handleSaveMarketplace} disabled={savingMarketplace || uploadingPhoto} data-testid="button-save-marketplace">
            {savingMarketplace ? "Saving..." : "Save Listing Details"}
          </Button>
        </CardContent>
      </Card>

      {/* Work photos gallery */}
      <Card data-testid="card-work-photos">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <ImagePlus className="h-4 w-4 text-gray-500" />
            Work Photos
          </CardTitle>
          <p className="text-xs text-gray-500">
            Show hosts examples of your work. Up to 10 photos. Thumbnail previews appear on your marketplace listing.
          </p>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          {workPhotoUrls.length > 0 && (
            <>
              <p className="text-xs text-gray-400" data-testid="text-drag-hint">Drag photos to reorder — the first photo shows first on your listing.</p>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleWorkPhotoDragEnd}>
                <SortableContext items={workPhotoUrls.map((url, i) => `${url}::${i}`)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-3 gap-2" data-testid="grid-work-photos">
                    {workPhotoUrls.map((url, idx) => (
                      <SortableWorkPhoto key={`${url}::${idx}`} url={url} idx={idx} sortableId={`${url}::${idx}`} onRemove={handleRemoveWorkPhoto} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          )}
          {workPhotoUrls.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-4" data-testid="text-no-work-photos">No work photos yet. Upload some to show hosts what you do.</p>
          )}
          <input
            ref={workPhotoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="hidden"
            onChange={handleUploadWorkPhoto}
            data-testid="input-work-photo"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400" data-testid="text-work-photo-count">{workPhotoUrls.length} / 10 photos</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (workPhotoUrls.length >= 10) {
                  setMarketplaceError("You can upload a maximum of 10 work photos.");
                  return;
                }
                workPhotoInputRef.current?.click();
              }}
              disabled={uploadingWorkPhoto || workPhotoUrls.length >= 10}
              data-testid="button-add-work-photo"
            >
              {uploadingWorkPhoto ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Uploading...</>
              ) : (
                <><Plus className="h-3.5 w-3.5 mr-1" />Add Photos</>
              )}
            </Button>
          </div>
          {workPhotoUrls.length >= 10 && (
            <p className="text-xs text-amber-600 font-medium" data-testid="text-work-photos-max">Maximum of 10 work photos reached.</p>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
